import { ref } from 'vue';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface UseAsyncOptions<F extends (...args: any) => Promise<any>> {
  immediate?: boolean;
  onResolved?: (value?: Awaited<ReturnType<F>>) => void;
  onRejected?: (reason: unknown) => void;
  displayLoadAfter?: number;
}
/**
 * A composable function that handles asynchronous operations with loading, error, and data states.
 * This composable does not handle parsing of errors, this should be done in the function that is passed to it. Look at the example for reference.
 *
 * @example
 * ```ts
 * const fetchData = async (arg1: string, arg2: number) => {
 *  return handleError(()=> fetch(`https://example.com/${arg1}/${arg2}`));
 * };
 *
 * const { data, error, loading, fetch } = useAsync(fetchData, [arg1, arg2], {
 *  immediate: true,
 *  onResolved: (value) => console.log(value),
 *  onRejected: (reason) => console.error(reason),
 * });
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useAsync = <F extends (...args: any) => Promise<any>>(
  fn: F,
  args?: Parameters<typeof fn>,
  options?: UseAsyncOptions<typeof fn>
) => {
  const opts = Object.assign({}, { immediate: true }, options);

  const data = ref<Awaited<ReturnType<typeof fn>>>();
  const error = ref<string>();
  const loading = ref(false);

  const fetch = async (...args: Parameters<typeof fn>) => {
    error.value = undefined;
    let finished = false;

    // This is added to support delaying the loading state, which sometimes is very.
    // These short loading states can cause flickering in the UI
    if (opts.displayLoadAfter) {
      setTimeout(() => {
        loading.value = !finished;
      }, opts.displayLoadAfter);
    } else {
      loading.value = !finished;
    }

    try {
      data.value = (await fn(...args)) as Awaited<ReturnType<typeof fn>>;
      if (opts.onResolved) {
        // handle void return type
        if (data.value === undefined) {
          opts.onResolved(undefined as Awaited<ReturnType<typeof fn>>);
        } else {
          opts.onResolved(data.value);
        }
      }
    } catch (err) {
      if (err instanceof Error) {
        error.value = err.message;
      }
      // TODO: Add logging of errors
      // console.error(err);
      if (opts.onRejected) {
        opts.onRejected(err);
      }
    } finally {
      finished = true;
      loading.value = !finished;
    }
  };

  if (opts.immediate) {
    if (args) void fetch(...args);
  }

  return { data, error, loading, fetch };
};
